import { IRecommendationSettings, IServiceItem } from 'types/recommendations.types';
import { LoadingOutlined, OrderedListOutlined, SelectOutlined, SettingOutlined } from '@ant-design/icons';
import { Modal, StepProps, Steps, Typography, notification } from 'antd';
import {
  RECOMMENDATION_SETTINGS_STEPS,
  SELECT_RECOMMENDATION,
  SETTINGS_RECOMMENDATION,
} from 'pages/Recommendations/constants';
import React, { ReactElement, useMemo, useState } from 'react';

import { AllRecommends } from 'pages/Recommendations/components/AllRecommends';
import { RecommendationsForm } from 'pages/Recommendations/components/RecommendationsForm';

type SettingsRecommendationModalProps<T> = {
  visible: boolean;
  onHide: () => void;
  children?: ReactElement | ReactElement[];
  onSubmit: (values: T & { items: IServiceItem[] }) => Promise<any>;
  onLoad: (params: any) => Promise<any>;
};

export const SettingsRecommendationModal = <T extends IRecommendationSettings>({
  children,
  onHide,
  visible,
  onSubmit,
  onLoad,
}: SettingsRecommendationModalProps<T>) => {
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState<T>();
  const [submitting, setSubmitting] = useState(false);

  const handleNextStep = async (values: T) => {
    setSettings(values);
    setStep((value) => value + 1);
  };

  const handlePreviousStep = () => {
    setStep((value) => value - 1);
  };

  const handleSubmit = async (items: IServiceItem[]) => {
    setSubmitting(true);
    if (!settings) return;
    return onSubmit({
      ...settings,
      items,
    })
      .then(() => {
        setStep(0);
        setSettings(undefined);
        onHide();
      })
      .catch((e) => {
        notification.error({
          message: 'Ошибка',
          duration: 2,
          description: e,
        });
      })
      .finally(() => setSubmitting(false));
  };

  const stepItems: StepProps[] = useMemo(() => {
    const stepName = RECOMMENDATION_SETTINGS_STEPS[step];
    return [
      {
        title: (
          <Typography.Title
            level={5}
            editable={
              stepName === SELECT_RECOMMENDATION &&
              !submitting && {
                editing: false,
                onStart: handlePreviousStep,
                tooltip: 'Назад к настройке',
                icon: <SelectOutlined />,
              }
            }
          >
            Настройка
          </Typography.Title>
        ),
        icon: <SettingOutlined />,
      },
      {
        title: (
          <>
            <Typography.Title level={5}>Выбор</Typography.Title>
          </>
        ),
        icon: submitting ? <LoadingOutlined /> : <OrderedListOutlined />,
      },
    ];
  }, [step, submitting]);

  return (
    <Modal
      centered
      open={visible}
      onCancel={onHide}
      footer={[]}
      width={RECOMMENDATION_SETTINGS_STEPS[step] === SETTINGS_RECOMMENDATION ? undefined : 900}
    >
      <Steps current={step} items={stepItems} size="small" type="navigation" style={{ marginBottom: 20 }} />
      {RECOMMENDATION_SETTINGS_STEPS[step] === SETTINGS_RECOMMENDATION && (
        <RecommendationsForm<T> initialValues={settings} onSubmit={handleNextStep}>
          {children}
        </RecommendationsForm>
      )}
      {RECOMMENDATION_SETTINGS_STEPS[step] === SELECT_RECOMMENDATION && (
        <AllRecommends
          onLoad={onLoad}
          cityIds={(settings as T).cityIds}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      )}
    </Modal>
  );
};
